// Only owner-scoped, allowlisted task codes enter this presentation. Never show
// raw provider output or imply that a failed task published a Coach message.
const CAPTURE_REASONS = {
  STORAGE_UNAVAILABLE: 'a temporary storage problem',
  EXTERNAL_COACH_TRANSACTIONS_REQUIRED: 'the database was temporarily unavailable',
  EXTERNAL_COACH_OFFLINE: 'your external Coach was offline',
  CURRENT_DAY_READ_LIMIT: "today's context was too large to gather",
  DAILY_TARGETS_READ_LIMIT: "today's context was too large to gather",
  READ_LIMIT: "today's context was too large to gather",
  DAILY_SNAPSHOT_CHANGED: 'your evidence kept changing while it was gathered',
  HOSTED_GENERATION_FAILED: 'the Coach service did not respond',
};
const CAPTURE_DENIALS = {
  REQUESTER_SCOPE_CHANGED: 'Coach access changed (for example your Dojo membership)',
  SCOPE_CHANGED: 'Coach access changed (for example your Dojo membership)',
  TASK_ROUTING_CHANGED: 'your Coach selection changed',
  READ_NOT_AUTHORIZED: 'Coach is not authorized to read the shared context it needs',
  CONVERSATION_CLEARED: 'Coach history was cleared',
  TASK_SOURCE_CHANGED: 'the saved check-in or its plan is no longer available to Coach',
  CREDENTIAL_REVOKED: 'your external Coach credential is no longer active',
};
// Retry applies to a task-backed or a no-task (capture) failure. A scheduled
// automatic renewal is pending recovery, never a manual Retry target.
export function dailyCoachRetryable(status) {
  if (!status || status.external_daily_task?.auto_retry === true) return false;
  return status.external_daily_task?.retryable === true
    || (!status.external_daily_task && status.external_daily_capture?.retryable === true);
}
// Only allowlisted definitive denials are refusals. Anything else (including
// a lost response) may have renewed the attempt: reconcile before resubmitting.
const RETRY_GUIDANCE = {
  TASK_SOURCE_CHANGED: 'The saved task no longer matches the current check-in, plan, or activity context. Refresh Coach to see the current status.',
  EXTERNAL_COACH_OFFLINE: 'Your external Coach is offline. Start it in Studio, then refresh Coach before retrying.',
  CREDENTIAL_REVOKED: 'Your external Coach credential is no longer active. Reconnect it before retrying.',
  SCOPE_CHANGED: 'Coach access changed. Refresh Coach before retrying.',
  REQUESTER_SCOPE_CHANGED: 'Coach access changed. Refresh Coach before retrying.',
  READ_NOT_AUTHORIZED: 'Coach is not authorized to read the shared context this insight needs. Check your Dojo sharing settings.',
  CONVERSATION_CLEARED: 'Coach history changed. Refresh Coach before retrying.',
  TASK_ROUTING_CHANGED: 'Your Coach selection changed. Refresh Coach before retrying.',
  EXTERNAL_COACH_AUTO_ACCEPTANCE_CONFLICT: 'Coach settings changed. Refresh Coach before retrying.',
  TASK_INVALID_PAYLOAD: 'The saved check-in could not be used for this retry. Refresh Coach.',
  TASK_RETRY_UNAVAILABLE: 'This daily insight can no longer be retried. Coach is showing its current status.',
  DAILY_CHECKIN_NOT_CURRENT: 'This check-in is no longer today\'s current check-in, so its insight was not retried. Coach is now showing your latest check-in.',
};
export const dailyCoachRetryDenied = code => Object.prototype.hasOwnProperty.call(RETRY_GUIDANCE, code);
export function dailyCoachRetryGuidance(code) {
  return RETRY_GUIDANCE[code] || 'Could not confirm the retry. Refresh Coach to check the saved task before trying again.';
}
export function dailyCoachFailureMessage(status) {
  const blocked = status?.external_daily_task?.retry_blocked_code || status?.external_daily_capture?.retry_blocked_code;
  if (blocked) return `Your check-in was saved, but no insight was posted. ${dailyCoachRetryGuidance(blocked)}`;
  const capture = !status?.external_daily_task && status?.external_daily_capture;
  if (capture?.disposition === 'exhausted') {
    const attempts = Number.isInteger(capture.attempts) && capture.attempts > 0 ? capture.attempts : 'several';
    return `Daily Coach insight could not be prepared after ${attempts} automatic attempts (${CAPTURE_REASONS[capture.code] || 'an unexpected error'}). Your check-in was saved, but no insight was posted.`;
  }
  if (capture?.disposition === 'denied') {
    return `Daily Coach insight was not prepared because ${CAPTURE_DENIALS[capture.code] || 'Coach access changed'}. Your check-in was saved, but no insight was posted.`;
  }
  const code = status?.external_daily_failure_code || status?.external_daily_task?.failure_code;
  if (code === 'TASK_INVALID_OUTPUT') {
    const detail = {
      TASK_OUTPUT_JSON: 'the Coach response was not valid JSON',
      TASK_OUTPUT_SCHEMA: 'the Coach response did not match the required format',
      TASK_OUTPUT_SEMANTIC: 'the Coach response did not meet the consistency rules',
      TASK_OUTPUT_SECURITY: 'the Coach response was blocked by a safety check',
      TASK_OUTPUT_SIZE: 'the Coach response exceeded the output limit',
    }[status?.external_daily_task?.failure_detail_code] || 'the Coach response could not be accepted';
    return `Daily Coach insight could not complete: ${detail}. Your check-in was saved, but no insight was posted.`;
  }
  // Drift can happen before claim, during inference or before publication.
  if (code === 'DAILY_SNAPSHOT_CHANGED') {
    return 'Daily Coach insight was not posted because your evidence changed since this attempt was captured. Your check-in was saved. Retry uses fresh authorized context.';
  }
  if (code === 'TASK_TIMED_OUT' || status?.external_daily_task?.status === 'timedout') {
    return 'Daily Coach insight timed out. Your check-in was saved, but no insight was posted.';
  }
  return 'Daily Coach insight could not complete. Your check-in was saved, but no insight was posted.';
}
